import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { MissionSchema } from "../schemas/mission.schema";
import { format, parse } from "date-fns";
import {
  frontendRewardTypeToBackend,
  parseRequirementQuantity,
} from "../utils/missionMapper";
import { MissionRewardType } from "../types/mission.type";

interface UseCreateMissionProps {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
}

const isTemporaryId = (value?: string) =>
  !value || value.startsWith("mission-field");

const parseDateField = (value?: string) => {
  if (!value) return undefined;
  try {
    const parsed = parse(value, "dd/MM/yyyy", new Date());
    return format(parsed, "yyyy-MM-dd");
  } catch {
    return value;
  }
};

const normalizeText = (value?: string) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const OPTIONAL_QUANTITY_LABELS = ["completar missao assim que iniciada"];

const isQuantityOptional = (label?: string) => {
  const normalized = normalizeText(label);
  if (!normalized) return false;
  return OPTIONAL_QUANTITY_LABELS.some((text) => normalized.includes(text));
};

const resolveRequirementQuantity = (
  condition: MissionSchema["conditions"][number],
) => {
  const parsed = parseRequirementQuantity(condition.value_label);
  if (parsed > 0) return parsed;
  return isQuantityOptional(condition.type_label) ? 1 : parsed;
};

const ensureUniqueRewards = (rewards: MissionSchema["rewards"]) => {
  const seenKeys = new Set<string>();
  rewards.forEach((reward) => {
    const rewardKey = reward.product_id || reward.reward_id;
    if (!rewardKey) return;
    const normalizedKey = String(rewardKey);
    if (seenKeys.has(normalizedKey)) {
      throw new Error(
        "Já existe uma recompensa com esse produto. Atualize ou remova o item duplicado antes de salvar.",
      );
    }
    seenKeys.add(normalizedKey);
  });
};

export const useCreateMission = ({
  onSuccess,
  onError,
}: UseCreateMissionProps = {}) => {
  const privateAxios = usePrivateAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createMission"],
    mutationFn: async (form: MissionSchema) => {
      ensureUniqueRewards(form.rewards || []);
      const maxPerUser = form.allow_multiple_times ? form.max_times : 1;
      const restrictEstablishments =
        form.mission_type === "establishment"
          ? form.restrict_establishments
          : false;

      const rewardsAttributes = (() => {
        const rewardKeyToIndex = new Map<string, number>();
        const normalizedRewards: Array<{
          id?: string;
          reward_type: string | null;
          reward_id?: string | null;
          quantity: number;
          properties: Record<string, unknown>;
        }> = [];

        form.rewards.forEach((rew) => {
          const rewardId = rew.product_id || rew.reward_id;
          const normalizedKey = rewardId ? String(rewardId) : undefined;
          const rewardProperties: Record<string, unknown> = {};
          if (rew.reward_label) rewardProperties.label = rew.reward_label;
          if (rew.product_name)
            rewardProperties.product_name = rew.product_name;
          const frontendRewardType = rew.reward_type as MissionRewardType;
          const backendRewardType =
            frontendRewardTypeToBackend(frontendRewardType);

          const payload = {
            id: isTemporaryId(rew.id) ? undefined : rew.id,
            reward_type:
              frontendRewardType === "points" ? null : backendRewardType,
            reward_id:
              frontendRewardType === "points" ? null : normalizedKey,
            quantity: Number(rew.quantity ?? 1),
            properties: rewardProperties,
          };

          if (normalizedKey) {
            if (rewardKeyToIndex.has(normalizedKey)) {
              const index = rewardKeyToIndex.get(normalizedKey)!;
              normalizedRewards[index] = payload;
              return;
            }
            rewardKeyToIndex.set(normalizedKey, normalizedRewards.length);
          }
          normalizedRewards.push(payload);
        });

        return normalizedRewards;
      })();

      const payload = {
        mission_type: form.mission_type,
        display_name: form.name,
        description: form.description,
        status: form.status,
        minimum_loyalty: form.minimum_loyalty,
        available_from: parseDateField(form.start_date),
        available_until: parseDateField(form.end_date),
        maximum_completions_per_user: maxPerUser || null,
        establishment_company_branch_id:
          form.mission_type === "establishment" ? form.establishment?.id : null,
        restrict_establishments: restrictEstablishments,
        allowed_establishment_company_branch_ids: restrictEstablishments
          ? form.allowed_establishments.map((item) => item.id)
          : [],
        requirements_attributes: form.conditions.map((cond) => {
          const conditionProperties = {
            ...(cond.properties || {}),
            ...(cond.type_label ? { label: cond.type_label } : {}),
            ...(cond.condition_group_label
              ? { condition_group_label: cond.condition_group_label }
              : {}),
            ...(cond.value_label ? { value_label: cond.value_label } : {}),
          };

          return {
            id: isTemporaryId(cond.id) ? undefined : cond.id,
            provider_klass: cond.provider_klass || cond.type_label,
            quantity: resolveRequirementQuantity(cond),
            condition_group_label: cond.condition_group_label || "general",
            properties: conditionProperties,
          };
        }),
        rewards_attributes: rewardsAttributes,
      };

      return privateAxios.post("/tmais/missions", { mission: payload });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["missions"] });
      onSuccess?.(response);
    },
    onError,
  });
};
